import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Eye, EyeOff, Loader2 } from 'lucide-react'

import { useAuth } from '@/shared/auth'
import { getAuthErrorMessage } from '@/shared/lib/auth-errors'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/shared/ui/field'
import { Input } from '@/shared/ui/input'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'

interface SignupFormProps extends React.ComponentProps<typeof Card> {
    onSwitchToLogin?: () => void
    onSuccess?: () => void
}

export function SignupForm({ onSwitchToLogin, onSuccess, ...props }: SignupFormProps) {
    const { t } = useTranslation()
    const { signUpWithEmail } = useAuth()
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showVerificationMessage, setShowVerificationMessage] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        // Validate passwords match
        if (password !== confirmPassword) {
            setError(t('auth.errors.passwordMismatch', 'Las contraseñas no coinciden'))
            return
        }

        // Validate password length
        if (password.length < 8) {
            setError(t('auth.errors.weakPassword', 'La contraseña debe tener al menos 8 caracteres'))
            return
        }

        setIsLoading(true)

        try {
            await signUpWithEmail(email, password)
            // Only show success message if signup was successful
            setShowVerificationMessage(true)
            // Call onSuccess after a short delay to show the message
            setTimeout(() => {
                onSuccess?.()
            }, 3000)
        } catch (err: unknown) {
            const errorMessage = getAuthErrorMessage(err, t, 'signup')

            console.error('Signup error:', { errorMessage, originalError: err })

            // Set error state and ensure consistent UI state
            setError(errorMessage)
            setShowVerificationMessage(false)
            
            // Prevent state thrashing by delaying the loading state update
            await new Promise(resolve => setTimeout(resolve, 100))
        } finally {
            setIsLoading(false)
        }
    }

    if (showVerificationMessage) {
        return (
            <Card {...props}>
                <div className='absolute right-4 top-4'>
                    <LanguageSwitchButton />
                </div>
                <CardHeader>
                    <CardTitle className='text-2xl'>{t('auth.signup.verifyEmail', 'Verifica tu email')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='rounded-md bg-green-50 p-4 text-sm text-green-800'>
                        <p className='mb-2 font-semibold'>{t('auth.signup.verifyEmail', 'Verifica tu email')}</p>
                        <p>
                            {t(
                                'auth.signup.checkInbox',
                                'Hemos enviado un email de verificación a tu bandeja de entrada. Por favor verifica tu email antes de continuar.',
                            )}
                        </p>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card {...props}>
            <div className='absolute right-4 top-4'>
                <LanguageSwitchButton />
            </div>
            <CardHeader>
                <CardTitle>{t('auth.signup.title', 'Crear Cuenta')}</CardTitle>
                <CardDescription>
                    {t('auth.signup.description', 'Ingresa tu información para crear tu cuenta')}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit}>
                    <FieldGroup>
                        {error && <div className='rounded-md bg-red-50 p-3 text-sm text-red-800'>{error}</div>}
                        <Field>
                            <FieldLabel htmlFor='email'>{t('auth.signup.email', 'Email')}</FieldLabel>
                            <Input
                                id='email'
                                type='email'
                                placeholder={t('auth.signup.emailPlaceholder', 'tu@email.com')}
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                disabled={isLoading}
                            />
                            <FieldDescription>
                                {t(
                                    'auth.signup.emailDescription',
                                    'Usaremos esto para contactarte. No compartiremos tu email con nadie.',
                                )}
                            </FieldDescription>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor='password'>{t('auth.signup.password', 'Contraseña')}</FieldLabel>
                            <div className='relative'>
                                <Input
                                    id='password'
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className='pr-10'
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowPassword(!showPassword)}
                                    className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none'
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                                </button>
                            </div>
                            <FieldDescription>
                                {t('auth.signup.passwordDescription', 'Debe tener al menos 8 caracteres.')}
                            </FieldDescription>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor='confirm-password'>
                                {t('auth.signup.confirmPassword', 'Confirmar Contraseña')}
                            </FieldLabel>
                            <div className='relative'>
                                <Input
                                    id='confirm-password'
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={e => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className='pr-10'
                                />
                                <button
                                    type='button'
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none'
                                    tabIndex={-1}
                                >
                                    {showConfirmPassword ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                                </button>
                            </div>
                            <FieldDescription>
                                {t('auth.signup.confirmPasswordDescription', 'Por favor confirma tu contraseña.')}
                            </FieldDescription>
                        </Field>
                        <FieldGroup>
                            <Field>
                                <Button type='submit' disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <Loader2 className='h-4 w-4 animate-spin' />
                                            {t('auth.signup.loading', 'Creando cuenta...')}
                                        </>
                                    ) : (
                                        t('auth.signup.submit', 'Crear Cuenta')
                                    )}
                                </Button>
                                <FieldDescription className='px-6 text-center'>
                                    {t('auth.signup.hasAccount', '¿Ya tienes cuenta?')}{' '}
                                    <button
                                        type='button'
                                        onClick={onSwitchToLogin}
                                        className='underline underline-offset-4'
                                    >
                                        {t('auth.signup.loginLink', 'Inicia sesión')}
                                    </button>
                                </FieldDescription>
                            </Field>
                        </FieldGroup>
                    </FieldGroup>
                </form>
            </CardContent>
        </Card>
    )
}
